import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import {
  Settings,
  Bell,
  Globe,
  CreditCard,
  Sliders,
  Shield,
  Palette,
  Building2,
  Sun,
  Moon,
  Monitor,
  ChevronRight,
  CalendarClock,
  AlertTriangle,
  DatabaseZap,
  Loader2,
  Plug,
} from '@/lib/icons';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { ApiErrorCard } from '@/components/feedback/ApiErrorCard';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setTenantSettings as setTenantSettingsRedux } from '@/features/tenants/tenant-slice';
import api, { handleApiResponse } from '@/lib/api';
import { OperationResult } from '@/lib/operation-result';
import {
  getTenantSettings,
  updateTenantSettings,
  type TenantSettingsValues,
} from '@/features/tenants/api';
import { cn } from '@/lib/utils';
import { hasMarkupRate } from '@/lib/markup';
import type { CreditTierInfoModel } from '@/features/customers/customer';
import { tierName } from '@/features/customers/tier';
import { useConfirmDialog } from '@/components/ui/confirm-dialog';
import { runDemoReset, type DemoResetResult } from '@/features/demo-reset/api';
import { isSuperAdmin } from '@/lib/auth/roles';

export const Route = createFileRoute('/_layout/settings/')({
  component: SettingsPage,
});

/* ── Types ───────────────────────────────────── */
interface PlanItem {
  /** The plan's own gate — never guessed from its name. */
  tier: string | null;
  name: string;
  maxTermDays: number;
  markupPercent: number;
  isActive: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function mapPlan(p: any): PlanItem {
  return {
    tier: p.minCreditTier ? String(p.minCreditTier).toUpperCase() : null,
    name: p.name || '',
    maxTermDays: p.termDays ?? (p.termMonths ? p.termMonths * 30 : 0),
    markupPercent: Number(p.markupPercentage ?? p.markupPercent ?? 0),
    isActive: p.isActive ?? true,
  };
}

/** cs-engine's tier ladder, for the names shown beside each plan's gate. */
async function fetchCreditTiers(): Promise<CreditTierInfoModel[]> {
  const result = await handleApiResponse<CreditTierInfoModel[]>(() =>
    api.get<OperationResult<CreditTierInfoModel[]>>('/v1/credit-tiers')
  );
  return result.succeeded && result.value ? result.value : [];
}

async function fetchPlans(): Promise<PlanItem[]> {
  const result = await handleApiResponse<any[]>(() =>
    api.get<OperationResult<any[]>>('/v1/financing-plans/')
  );
  if (result.succeeded && result.value) {
    return result.value.map(mapPlan);
  }
  return [];
}

const tierColors: Record<string, { badge: string; ring: string }> = {
  A: {
    badge: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
    ring: 'ring-emerald-500/30',
  },
  B: {
    badge: 'bg-blue-500/10 text-blue-600 border-blue-500/30',
    ring: 'ring-blue-500/30',
  },
  C: {
    badge: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
    ring: 'ring-amber-500/30',
  },
  D: {
    badge: 'bg-orange-500/10 text-orange-600 border-orange-500/30',
    ring: 'ring-orange-500/30',
  },
  E: {
    badge: 'bg-red-500/10 text-red-600 border-red-500/30',
    ring: 'ring-red-500/30',
  },
};

/* ── Notification toggle row ─────────────────── */
function NotificationRow({
  event,
  description,
  enabled,
  onToggle,
  saving,
}: {
  event: string;
  description: string;
  enabled: boolean;
  onToggle: (enabled: boolean) => void;
  saving: boolean;
}) {
  return (
    <div className="flex items-center justify-between py-3.5">
      <div className="min-w-0 mr-4">
        <p className="text-sm font-medium">{event}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch checked={enabled} onCheckedChange={onToggle} disabled={saving} />
    </div>
  );
}

function SettingsPage() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const tenantSettings = useAppSelector((state) => state.tenant.settings);
  const currentUser = useAppSelector((state) => state.auth.user);
  // Platform SuperAdmin only (bypass-permissions role, not tenant-scoped).
  const isCrossTenantOperator =
    isSuperAdmin(currentUser) && !currentUser?.tenantId;

  const [demoResetRun, setDemoResetRun] = useState<DemoResetResult | null>(
    null
  );

  const demoResetConfirm = useConfirmDialog({
    title: 'Reset demo environment?',
    description:
      'Wipes ALL BPL test data (offerings, scoring & loan schemas), re-seeds the canonical test customers (Nasri & Aisha), and resets the transsion / hormuud mocks. This cannot be undone.',
    confirmText: 'Reset environment',
    cancelText: 'Cancel',
    variant: 'destructive',
  });

  const demoResetMutation = useMutation({
    mutationFn: runDemoReset,
    onSuccess: (result) => {
      if (result.value) setDemoResetRun(result.value);
      const ok =
        result.succeeded &&
        result.value?.exitCode === 0 &&
        !result.value?.timedOut;
      if (ok) {
        toast({
          title: t('common.success'),
          description: `Demo environment reset in ${(
            (result.value?.durationMs ?? 0) / 1000
          ).toFixed(1)}s`,
        });
      } else {
        toast({
          title: t('common.error'),
          description:
            result.errors?.[0] ??
            'Reset finished with errors — see the run log',
          variant: 'destructive',
        });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: t('common.error'),
        description:
          error instanceof Error ? error.message : 'Reset request failed',
        variant: 'destructive',
      });
    },
  });

  // Theme state
  const [theme, setTheme] = useState<'light' | 'dark' | 'system'>(() => {
    return (localStorage.getItem('theme') as 'light' | 'dark') || 'system';
  });

  const { data: plansRaw, isLoading: plansLoading } = useQuery({
    queryKey: ['financing-plans-settings-overview'],
    queryFn: fetchPlans,
  });
  const plans = Array.isArray(plansRaw) ? plansRaw : [];
  // Header and cells hide together, so the table never misaligns.
  const anyPlanHasMarkup = plans.some((p) => hasMarkupRate(p.markupPercent));

  // Reference data — changes about never, so it can sit stale for a while.
  const { data: creditTiers } = useQuery({
    queryKey: ['credit-tiers'],
    queryFn: fetchCreditTiers,
    staleTime: 3_600_000,
  });

  const {
    data: settingsResult,
    isLoading: settingsLoading,
    isError: settingsError,
    error: settingsErr,
    refetch: refetchSettings,
  } = useQuery({
    queryKey: ['tenant-settings'],
    queryFn: getTenantSettings,
  });

  const settings = settingsResult?.value ?? tenantSettings;

  // Quick-update mutation for individual toggles
  const quickSave = useMutation({
    mutationFn: (partial: Partial<TenantSettingsValues>) =>
      updateTenantSettings(partial),
    onSuccess: (result) => {
      if (result.succeeded) {
        queryClient.invalidateQueries({ queryKey: ['tenant-settings'] });
        if (result.value) {
          dispatch(setTenantSettingsRedux(result.value));
        }
        toast({
          title: t('common.success'),
          description: 'Setting updated successfully',
        });
      } else {
        toast({
          title: t('common.error'),
          description: result.errors?.[0] ?? 'Failed to update setting',
          variant: 'destructive',
        });
      }
    },
  });

  const toggleNotification = (
    key: keyof TenantSettingsValues,
    enabled: boolean
  ) => {
    quickSave.mutate({ [key]: enabled });
  };

  const handleThemeChange = (newTheme: 'light' | 'dark' | 'system') => {
    setTheme(newTheme);
    if (newTheme === 'system') {
      localStorage.removeItem('theme');
      const prefersDark = window.matchMedia(
        '(prefers-color-scheme: dark)'
      ).matches;
      document.documentElement.classList.toggle('dark', prefersDark);
    } else {
      localStorage.setItem('theme', newTheme);
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
          <p className="text-muted-foreground text-sm mt-1">
            Platform configuration, notifications & preferences
          </p>
        </div>
        <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center text-primary-foreground shadow-lg">
          <Settings className="h-5 w-5" />
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Link to="/settings/tenant">
          <Card className="shadow-sm border-0 hover:shadow-md transition-all duration-200 cursor-pointer group">
            <CardContent className="flex items-center gap-3 py-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-indigo-500 to-indigo-600 text-white shadow-lg">
                <Building2 className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Tenant Settings</p>
                <p className="text-xs text-muted-foreground">
                  Business info, financing & risk
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/settings/branches">
          <Card className="shadow-sm border-0 hover:shadow-md transition-all duration-200 cursor-pointer group">
            <CardContent className="flex items-center gap-3 py-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-teal-500 to-teal-600 text-white shadow-lg">
                <Globe className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Branches</p>
                <p className="text-xs text-muted-foreground">
                  Manage locations & addresses
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/settings/installment">
          <Card className="shadow-sm border-0 hover:shadow-md transition-all duration-200 cursor-pointer group">
            <CardContent className="flex items-center gap-3 py-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 text-white shadow-lg">
                <CalendarClock className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Installment Settings</p>
                <p className="text-xs text-muted-foreground">
                  Payment frequencies & escalation
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/settings/policies">
          <Card className="shadow-sm border-0 hover:shadow-md transition-all duration-200 cursor-pointer group">
            <CardContent className="flex items-center gap-3 py-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg">
                <Sliders className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Policy Rules</p>
                <p className="text-xs text-muted-foreground">
                  Retry counts, grace periods & thresholds
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/settings/action-controls">
          <Card className="shadow-sm border-0 hover:shadow-md transition-all duration-200 cursor-pointer group">
            <CardContent className="flex items-center gap-3 py-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg">
                <Shield className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Action Controls</p>
                <p className="text-xs text-muted-foreground">
                  Device lifecycle actions & role ownership
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/settings/operations">
          <Card className="shadow-sm border-0 hover:shadow-md transition-all duration-200 cursor-pointer group">
            <CardContent className="flex items-center gap-3 py-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-lg">
                <DatabaseZap className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">Operations Playbook</p>
                <p className="text-xs text-muted-foreground">
                  Payment scenarios, channels & flows
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>
        <Link to="/settings/odoo">
          <Card className="shadow-sm border-0 hover:shadow-md transition-all duration-200 cursor-pointer group">
            <CardContent className="flex items-center gap-3 py-4">
              <div className="p-2 rounded-xl bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-lg">
                <Plug className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold">
                  {t('odoo.settings.cardTitle')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {t('odoo.settings.cardDescription')}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
            </CardContent>
          </Card>
        </Link>
      </div>

      {/* Financing Plan Configuration */}
      <Card className="shadow-md border-0">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 text-white shadow-lg">
              <CreditCard className="h-4 w-4" />
            </div>
            <div>
              <CardTitle className="text-lg">
                Financing Plan Configuration
              </CardTitle>
              <CardDescription>
                Available financing plans and their term limits
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {plansLoading ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 rounded" />
              ))}
            </div>
          ) : plans.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No financing plans configured</p>
              <p className="text-sm">Add financing plans in the backend.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead>Plan</TableHead>
                    <TableHead>Max Term</TableHead>
                    {anyPlanHasMarkup && <TableHead>Markup Rate</TableHead>}
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {plans.map((t, idx) => {
                    const tc = (t.tier && tierColors[t.tier]) || tierColors.A;
                    return (
                      <TableRow
                        key={idx}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {t.tier && (
                              <span
                                className={cn(
                                  'inline-flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold border',
                                  tc.badge
                                )}
                                title={`Requires tier ${t.tier} ${tierName(t.tier, creditTiers)} or better`}
                              >
                                {t.tier}
                              </span>
                            )}
                            <span className="font-medium text-sm">
                              {t.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {t.maxTermDays} days
                        </TableCell>
                        {anyPlanHasMarkup && (
                          <TableCell>
                            <div className="flex items-center gap-1.5 font-medium">
                              {t.markupPercent}%
                              <Sliders className="h-3 w-3 text-muted-foreground" />
                            </div>
                          </TableCell>
                        )}
                        <TableCell>
                          <Badge
                            className={
                              t.isActive
                                ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
                                : 'bg-gray-500/15 text-gray-600 border-gray-500/30'
                            }
                          >
                            {t.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* General Overview */}
        <Card className="shadow-md border-0">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 text-white shadow-lg">
                <Globe className="h-4 w-4" />
              </div>
              <CardTitle className="text-lg">General</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            {settingsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-10 rounded" />
                ))}
              </div>
            ) : settingsError ? (
              <ApiErrorCard
                error={settingsErr}
                onRetry={() => refetchSettings()}
                title="Failed to load settings"
              />
            ) : (
              <>
                <div className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium">Company Name</p>
                    <p className="text-xs text-muted-foreground">
                      Organization display name
                    </p>
                  </div>
                  <span className="text-sm font-medium text-foreground">
                    {settings?.companyName || '—'}
                  </span>
                </div>
                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
                <div className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium">Currency</p>
                    <p className="text-xs text-muted-foreground">
                      Active currency for transactions
                    </p>
                  </div>
                  <Badge className="bg-primary/10 text-primary border-primary/20">
                    {settings?.currency || 'USD'}
                  </Badge>
                </div>
                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
                <div className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium">Timezone</p>
                    <p className="text-xs text-muted-foreground">
                      Regional time configuration
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground font-mono bg-muted px-2 py-1 rounded">
                    {settings?.timezone || 'UTC'}
                  </span>
                </div>
                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
                <div className="flex items-center justify-between py-2.5">
                  <div>
                    <p className="text-sm font-medium">Locale</p>
                    <p className="text-xs text-muted-foreground">
                      Default language for notifications
                    </p>
                  </div>
                  <span className="text-sm font-medium">
                    {settings?.locale === 'so'
                      ? '🇸🇴 Soomaali'
                      : '🇺🇸 English'}
                  </span>
                </div>
                <div className="pt-2">
                  <Link to="/settings/tenant">
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2 w-full"
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Edit in Tenant Settings
                    </Button>
                  </Link>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Notification Settings */}
        <Card className="shadow-md border-0">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 text-white shadow-lg">
                <Bell className="h-4 w-4" />
              </div>
              <div>
                <CardTitle className="text-lg">Notification Channels</CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Toggle which events trigger notifications
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-0">
            {settingsLoading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-12 rounded" />
                ))}
              </div>
            ) : settingsError ? (
              <ApiErrorCard
                error={settingsErr}
                onRetry={() => refetchSettings()}
                title="Failed to load notification settings"
              />
            ) : (
              <>
                <NotificationRow
                  event="Application Submitted"
                  description="Notify when a customer submits a financing application"
                  enabled={settings?.notifyOnApplicationSubmitted ?? true}
                  onToggle={(v) =>
                    toggleNotification('notifyOnApplicationSubmitted', v)
                  }
                  saving={quickSave.isPending}
                />
                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
                <NotificationRow
                  event="Payment Received"
                  description="Notify when an installment payment is collected"
                  enabled={settings?.notifyOnPaymentReceived ?? true}
                  onToggle={(v) =>
                    toggleNotification('notifyOnPaymentReceived', v)
                  }
                  saving={quickSave.isPending}
                />
                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
                <NotificationRow
                  event="Payment Overdue"
                  description="Notify when a payment becomes overdue"
                  enabled={settings?.notifyOnPaymentOverdue ?? true}
                  onToggle={(v) =>
                    toggleNotification('notifyOnPaymentOverdue', v)
                  }
                  saving={quickSave.isPending}
                />
                <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
                <NotificationRow
                  event="Contract Completed"
                  description="Notify when all payments are made and device unlocks"
                  enabled={settings?.notifyOnContractCompleted ?? true}
                  onToggle={(v) =>
                    toggleNotification('notifyOnContractCompleted', v)
                  }
                  saving={quickSave.isPending}
                />
              </>
            )}
          </CardContent>
        </Card>

        {/* Security */}
        <Card className="shadow-md border-0">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg">
                <Shield className="h-4 w-4" />
              </div>
              <CardTitle className="text-lg">Security</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium">Auto-Lock Devices</p>
                <p className="text-xs text-muted-foreground">
                  Lock devices when payments become overdue
                </p>
              </div>
              {settingsLoading ? (
                <Skeleton className="h-5 w-10" />
              ) : (
                <Badge
                  className={
                    settings?.autoLockEnabled
                      ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
                      : 'bg-gray-500/15 text-gray-600 border-gray-500/30'
                  }
                >
                  {settings?.autoLockEnabled ? 'Enabled' : 'Disabled'}
                </Badge>
              )}
            </div>
            <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
            <div className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium">Lock After</p>
                <p className="text-xs text-muted-foreground">
                  Days overdue before device locks
                </p>
              </div>
              <span className="text-sm text-muted-foreground font-mono">
                {settings?.autoLockAfterDays ?? '—'} days
              </span>
            </div>
            <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
            <div className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium">Max Active Contracts</p>
                <p className="text-xs text-muted-foreground">
                  Per customer contract limit
                </p>
              </div>
              <span className="text-sm text-muted-foreground font-mono">
                {settings?.maxActiveContracts ?? '—'}
              </span>
            </div>
            <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
            <div className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium">Grace Period</p>
                <p className="text-xs text-muted-foreground">
                  Days after due before late fees
                </p>
              </div>
              <span className="text-sm text-muted-foreground font-mono">
                {settings?.gracePeriodDays ?? '—'} days
              </span>
            </div>
            <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
            <div className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium">Cancellation Window</p>
                <p className="text-xs text-muted-foreground">
                  Hours after signing a contract can be cancelled
                </p>
              </div>
              <span className="text-sm text-muted-foreground font-mono">
                {settings?.cancellationWindowHours ?? '—'} hours
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Appearance */}
        <Card className="shadow-md border-0">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-pink-500 to-pink-600 text-white shadow-lg">
                <Palette className="h-4 w-4" />
              </div>
              <CardTitle className="text-lg">Appearance</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="space-y-1">
            <div className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium">Theme</p>
                <p className="text-xs text-muted-foreground">
                  Choose your preferred theme
                </p>
              </div>
              <div className="flex gap-1">
                {[
                  { value: 'light' as const, icon: Sun, label: 'Light' },
                  { value: 'dark' as const, icon: Moon, label: 'Dark' },
                  { value: 'system' as const, icon: Monitor, label: 'System' },
                ].map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleThemeChange(opt.value)}
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition-all duration-200',
                      theme === opt.value
                        ? 'bg-primary/15 text-primary ring-1 ring-primary/30'
                        : 'text-muted-foreground hover:bg-muted'
                    )}
                  >
                    <opt.icon className="h-3.5 w-3.5" />
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
            <div className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium">Payment Methods</p>
                <p className="text-xs text-muted-foreground">
                  Accepted payment methods
                </p>
              </div>
              <div className="flex gap-1.5 flex-wrap justify-end">
                {(settings?.paymentMethods ?? ['mobile_money', 'cash']).map(
                  (m) => (
                    <Badge
                      key={m}
                      className="bg-primary/10 text-primary border-primary/20 text-[10px]"
                    >
                      {m === 'mobile_money'
                        ? 'Mobile Money'
                        : m === 'cash'
                          ? 'Cash'
                          : m === 'bank_transfer'
                            ? 'Bank Transfer'
                            : m}
                    </Badge>
                  )
                )}
              </div>
            </div>
            <div className="h-px bg-[hsl(var(--foreground)/0.06)]" />
            <div className="flex items-center justify-between py-2.5">
              <div>
                <p className="text-sm font-medium">Partial Payments</p>
                <p className="text-xs text-muted-foreground">
                  Allow customers to pay less than full installment
                </p>
              </div>
              {settingsLoading ? (
                <Skeleton className="h-5 w-10" />
              ) : (
                <Badge
                  className={
                    settings?.allowPartialPayments
                      ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
                      : 'bg-gray-500/15 text-gray-600 border-gray-500/30'
                  }
                >
                  {settings?.allowPartialPayments ? 'Allowed' : 'Disabled'}
                </Badge>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {isCrossTenantOperator && (
        <Card className="shadow-md border border-destructive/30 bg-destructive/[0.02]">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-red-500 to-red-600 text-white shadow-lg">
                <AlertTriangle className="h-4 w-4" />
              </div>
              <div>
                <CardTitle className="text-lg">Danger Zone</CardTitle>
                <CardDescription>
                  SuperAdmin-only developer tooling — destructive and
                  irreversible
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 rounded-lg border border-destructive/20 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-semibold flex items-center gap-2">
                  <DatabaseZap className="h-4 w-4 text-destructive" />
                  Reset demo environment
                </p>
              </div>
              <Button
                variant="destructive"
                className="gap-2 shrink-0"
                disabled={demoResetMutation.isPending}
                onClick={async () => {
                  const confirmed = await demoResetConfirm.confirm();
                  if (confirmed) demoResetMutation.mutate();
                }}
              >
                {demoResetMutation.isPending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Resetting…
                  </>
                ) : (
                  <>
                    <DatabaseZap className="h-4 w-4" />
                    Reset environment
                  </>
                )}
              </Button>
            </div>

            {demoResetRun && (
              <div className="flex items-center gap-2 text-xs">
                <Badge
                  className={
                    demoResetRun.exitCode === 0 && !demoResetRun.timedOut
                      ? 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30'
                      : 'bg-red-500/15 text-red-600 border-red-500/30'
                  }
                >
                  {demoResetRun.timedOut
                    ? 'Timed out'
                    : demoResetRun.exitCode === 0
                      ? 'Success'
                      : `Exit ${demoResetRun.exitCode}`}
                </Badge>
                <span className="text-muted-foreground">
                  {(demoResetRun.durationMs / 1000).toFixed(1)}s
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <demoResetConfirm.ConfirmDialog />
    </div>
  );
}
